import { usePopularImages } from "@features/popular/use-popular";
import { useEffectPlease } from "@features/utils";
import { Heading } from "@radix-ui/themes";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Masonry } from "@components/masonry";
import { TemplatePackTile } from "@components/template-pack-tile";
import { createCustomTileItem } from "./custom-tile";

type PopularItem = {
  url: string;
  name?: string;
  description?: string;
  category?: string;
  image_size?: { width: number; height: number };
};

// Categories for popular items (will be populated from API in the future)
const PopularCategories: Record<string, { name: string }> = {
  // Example structure for when categories are available:
  // trending: { name: "Trending" },
  // seasonal: { name: "Seasonal" },
  // classic: { name: "Classic" },
};

type PopularItemsGridProps = {
  searchQuery: string;
  mode: "tryon" | "haircut" | "image";
  imageType: "image_outfit" | "image_hairstyle" | "image_image";
};

export const PopularItemsGrid = ({
  searchQuery,
  mode,
  imageType,
}: PopularItemsGridProps) => {
  const { t } = useTranslation();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    usePopularImages(imageType, "");
  const observerRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const { site } = useParams();

  useEffectPlease(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const allImages: PopularItem[] = (
    data?.pages.flatMap((page) => page) || []
  ).filter((item) => {
    if (!searchQuery) return true;
    const itemName = (
      item.name ||
      item.description ||
      item.url.split("/").pop()?.split(".")[0] ||
      "Image"
    ).toLowerCase();
    return itemName.includes(searchQuery.toLowerCase());
  });

  // Group items by category (for future use)
  const groupedItems = allImages.reduce((acc, item) => {
    const category = item.category || "all";
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(item);
    return acc;
  }, {} as Record<string, PopularItem[]>);

  // Get category keys - use PopularCategories order if available, otherwise just "all"
  const categoryKeys =
    Object.keys(PopularCategories).length > 0
      ? Object.keys(PopularCategories).filter(
          (key) => groupedItems[key]?.length > 0
        )
      : Object.keys(groupedItems);

  // Build navigation URL based on mode
  const getNavigationUrl = (imageUrl: string) => {
    const baseParams = `mode=${mode}&format=auto&image=${encodeURIComponent(
      imageUrl
    )}`;
    if (mode === "image") {
      return `/${site}/camera/new?${baseParams}&similarity=20`;
    }
    return `/${site}/camera/new?${baseParams}`;
  };

  // Custom tile for the first item
  const customTile = createCustomTileItem(
    t("home.custom_image"),
    `/camera/new?mode=${mode}`
  );

  // Render a single category's masonry grid
  const renderCategoryGrid = (
    items: PopularItem[],
    categoryKey?: string,
    includeCustom?: boolean
  ) => {
    const imageItems = items.map((item) => ({
      width: item.image_size?.width || 512,
      height: item.image_size?.height || 512,
      id: categoryKey ? `${categoryKey}-${item.url}` : item.url,
      render: (
        <TemplatePackTile
          key={item.url}
          name={
            item.name ||
            item.description ||
            item.url.split("/").pop()?.split(".")[0] ||
            "Image"
          }
          photoCount={1}
          imageUrl={item.url}
          onClick={() => navigate(getNavigationUrl(item.url))}
        />
      ),
    }));

    const images = includeCustom ? [customTile, ...imageItems] : imageItems;

    return <Masonry size="2" className="-mx-1" images={images} />;
  };

  // Check if we have actual categories from the API
  const hasCategories = Object.keys(PopularCategories).length > 0;

  return (
    <div className="space-y-8">
      {/* Render with categories if available, otherwise flat list */}
      {hasCategories
        ? categoryKeys.map((categoryKey, index) => {
            const items = groupedItems[categoryKey];
            if (!items || items.length === 0) return null;

            return (
              <div key={categoryKey}>
                <Heading size="4" className="mb-3">
                  {PopularCategories[categoryKey]?.name || categoryKey}
                </Heading>
                {renderCategoryGrid(
                  items,
                  categoryKey,
                  !searchQuery && index === 0
                )}
              </div>
            );
          })
        : // Flat list when no categories
          renderCategoryGrid(allImages, undefined, !searchQuery)}

      <div ref={observerRef} className="h-4 w-full" />
    </div>
  );
};

// Convenience wrapper components
export const TryonGrid = ({ searchQuery }: { searchQuery: string }) => (
  <PopularItemsGrid
    searchQuery={searchQuery}
    mode="tryon"
    imageType="image_outfit"
  />
);

export const HaircutsGrid = ({ searchQuery }: { searchQuery: string }) => (
  <PopularItemsGrid
    searchQuery={searchQuery}
    mode="haircut"
    imageType="image_hairstyle"
  />
);

export const MemesGrid = ({ searchQuery }: { searchQuery: string }) => (
  <PopularItemsGrid
    searchQuery={searchQuery}
    mode="image"
    imageType="image_image"
  />
);
